import {
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { type ApiError, isAbortError } from "@/lib/api/errors";
import {
  type AnalysisResponse,
  getPolizzaResult,
  getPolizzaStatus,
  getRunResult,
  getRunResultByUrl,
  getRunStatus,
  getRunStatusByUrl,
  type PolizzaExtractResponse,
  type RunStatus,
} from "@/lib/api/extract";
import { triggerPdfDownload } from "@/lib/pdf";
import { buildRunReportFileName } from "@/lib/pdf-filenames";
import type { DocumentKind, UnifiedRunStatus } from "./status-domain";
import type { RefreshRunsResult } from "./useRunsHistory";

export type RunLinks = {
  statusUrl: string;
  resultUrl: string;
};

export type ActiveRunTarget = {
  id: number;
  kind: DocumentKind;
};

type RefreshRunsFn = (options?: {
  silent?: boolean;
}) => Promise<RefreshRunsResult>;

type SetResultState = {
  setAnalysis: (value: AnalysisResponse | null) => void;
  setPolizzaAnalysis: (value: PolizzaExtractResponse | null) => void;
};

const POLL_INTERVAL_MS = 2000;
const COMPLETED_RESULT_RETRY_INTERVAL_MS = 3000;
const COMPLETED_RESULT_MAX_ATTEMPTS = 10;

function areRunStatusesEqual(
  left: UnifiedRunStatus | null,
  right: UnifiedRunStatus | null,
): boolean {
  if (left === right) return true;
  if (!left || !right) return false;

  return (
    left.kind === right.kind &&
    left.id === right.id &&
    left.status === right.status &&
    left.inputFilename === right.inputFilename &&
    left.renderDpiUsed === right.renderDpiUsed &&
    left.totalPages === right.totalPages &&
    left.processedPages === right.processedPages &&
    left.failedPages === right.failedPages &&
    left.currentPage === right.currentPage &&
    left.progressPct === right.progressPct &&
    left.startedAt === right.startedAt &&
    left.finishedAt === right.finishedAt &&
    left.lastError === right.lastError
  );
}

export function toUnifiedRunStatus(
  kind: DocumentKind,
  status: {
    run_id?: number;
    polizza_id?: number;
    status: RunStatus;
    input_filename?: string | null;
    render_dpi_used?: number | null;
    total_pages?: number;
    processed_pages?: number;
    failed_pages?: number;
    current_page?: number | null;
    progress_pct?: number;
    started_at?: string | null;
    finished_at?: string | null;
    last_error?: string | null;
  },
): UnifiedRunStatus {
  const id =
    kind === "fatture_ddt" ? (status.run_id ?? 0) : (status.polizza_id ?? 0);

  return {
    kind,
    id,
    status: status.status,
    inputFilename: status.input_filename ?? null,
    renderDpiUsed: status.render_dpi_used ?? null,
    totalPages: status.total_pages ?? 0,
    processedPages: status.processed_pages ?? 0,
    failedPages: status.failed_pages ?? 0,
    currentPage: status.current_page ?? null,
    progressPct: status.progress_pct ?? 0,
    startedAt: status.started_at ?? null,
    finishedAt: status.finished_at ?? null,
    lastError: status.last_error ?? null,
  };
}

export function useCompletedResultRecovery({
  activeRunRef,
  onUnauthorized,
  setAnalysis,
  setPolizzaAnalysis,
  setCompletedResultError,
  setIsFetchingCompletedResult,
  cachePdf,
}: {
  activeRunRef: MutableRefObject<ActiveRunTarget | null>;
  onUnauthorized: (message?: string) => void;
  setCompletedResultError: (value: string | null) => void;
  setIsFetchingCompletedResult: (value: boolean) => void;
  cachePdf: (runId: number, pdfBase64: string) => void;
} & SetResultState) {
  const completedResultRetryTimeoutRef = useRef<number | null>(null);
  const completedResultRetryAttemptRef = useRef(0);
  const completedResultRequestSeqRef = useRef(0);

  const clearCompletedResultRetryTimer = useCallback(() => {
    if (completedResultRetryTimeoutRef.current !== null) {
      window.clearTimeout(completedResultRetryTimeoutRef.current);
      completedResultRetryTimeoutRef.current = null;
    }
  }, []);

  const stopCompletedResultRecovery = useCallback(() => {
    clearCompletedResultRetryTimer();
    completedResultRetryAttemptRef.current = 0;
    setIsFetchingCompletedResult(false);
  }, [clearCompletedResultRetryTimer, setIsFetchingCompletedResult]);

  const resetCompletedResultRecoveryState = useCallback(() => {
    completedResultRequestSeqRef.current += 1;
    stopCompletedResultRecovery();
    setCompletedResultError(null);
  }, [setCompletedResultError, stopCompletedResultRecovery]);

  const fetchCompletedResultOnce = useCallback(
    async (
      kind: DocumentKind,
      id: number,
      links: RunLinks | null,
    ): Promise<boolean> => {
      try {
        if (kind === "fatture_ddt") {
          const result = links
            ? await getRunResultByUrl(links.resultUrl)
            : await getRunResult(id);
          if (
            activeRunRef.current?.id !== id ||
            activeRunRef.current?.kind !== kind
          ) {
            return true;
          }
          setAnalysis(result);
          setPolizzaAnalysis(null);
          if (result.pdf_base64) {
            cachePdf(id, result.pdf_base64);
          }
        } else {
          const result = await getPolizzaResult(id);
          if (
            activeRunRef.current?.id !== id ||
            activeRunRef.current?.kind !== kind
          ) {
            return true;
          }
          setPolizzaAnalysis(result);
          setAnalysis(null);
        }

        setCompletedResultError(null);
        stopCompletedResultRecovery();
        return true;
      } catch (error) {
        const apiError = error as ApiError;
        if (apiError.status === 401) {
          onUnauthorized();
          return true;
        }
        return false;
      }
    },
    [
      activeRunRef,
      cachePdf,
      onUnauthorized,
      setAnalysis,
      setCompletedResultError,
      setPolizzaAnalysis,
      stopCompletedResultRecovery,
    ],
  );

  const startCompletedResultRecovery = useCallback(
    (kind: DocumentKind, id: number, links: RunLinks | null) => {
      resetCompletedResultRecoveryState();
      setIsFetchingCompletedResult(true);

      const requestSeq = completedResultRequestSeqRef.current;

      const attemptFetch = async () => {
        if (completedResultRequestSeqRef.current !== requestSeq) return;
        if (
          activeRunRef.current?.id !== id ||
          activeRunRef.current?.kind !== kind
        ) {
          stopCompletedResultRecovery();
          return;
        }

        const resolved = await fetchCompletedResultOnce(kind, id, links);
        if (completedResultRequestSeqRef.current !== requestSeq) return;
        if (
          activeRunRef.current?.id !== id ||
          activeRunRef.current?.kind !== kind
        ) {
          return;
        }
        if (resolved) return;

        completedResultRetryAttemptRef.current += 1;
        if (
          completedResultRetryAttemptRef.current >=
          COMPLETED_RESULT_MAX_ATTEMPTS
        ) {
          setIsFetchingCompletedResult(false);
          setCompletedResultError(
            "Analisi completata, ma risultato non disponibile.",
          );
          return;
        }

        completedResultRetryTimeoutRef.current = window.setTimeout(() => {
          void attemptFetch();
        }, COMPLETED_RESULT_RETRY_INTERVAL_MS);
      };

      void attemptFetch();
    },
    [
      activeRunRef,
      fetchCompletedResultOnce,
      resetCompletedResultRecoveryState,
      setCompletedResultError,
      setIsFetchingCompletedResult,
      stopCompletedResultRecovery,
    ],
  );

  useEffect(() => {
    return () => {
      completedResultRequestSeqRef.current += 1;
      clearCompletedResultRetryTimer();
    };
  }, [clearCompletedResultRetryTimer]);

  return {
    resetCompletedResultRecoveryState,
    startCompletedResultRecovery,
    stopCompletedResultRecovery,
  };
}

export function useRunPdfDownload({
  analysis,
  onUnauthorized,
  runStatus,
}: {
  analysis: AnalysisResponse | null;
  onUnauthorized: (message?: string) => void;
  runStatus: UnifiedRunStatus | null;
}) {
  const [downloadingPdfRunId, setDownloadingPdfRunId] = useState<number | null>(
    null,
  );
  const pdfByRunIdRef = useRef<Map<number, string>>(new Map());

  const cachePdf = useCallback((runId: number, pdfBase64: string) => {
    pdfByRunIdRef.current.set(runId, pdfBase64);
  }, []);

  const clearPdfCache = useCallback(() => {
    pdfByRunIdRef.current.clear();
    setDownloadingPdfRunId(null);
  }, []);

  const downloadRunPdf = useCallback(
    async (runId: number, kind: DocumentKind): Promise<void> => {
      if (kind !== "fatture_ddt") {
        throw new Error("PDF disponibile solo per analisi fatture/DDT.");
      }

      try {
        setDownloadingPdfRunId(runId);
        let pdfBase64 = pdfByRunIdRef.current.get(runId) ?? null;
        let originalFilename =
          runStatus?.id === runId ? runStatus.inputFilename : null;

        if (
          !pdfBase64 &&
          runStatus?.id === runId &&
          runStatus.kind === "fatture_ddt" &&
          analysis?.pdf_base64
        ) {
          pdfBase64 = analysis.pdf_base64;
        }

        if (!pdfBase64) {
          const result = await getRunResult(runId);
          pdfBase64 = result.pdf_base64;
          originalFilename = result.input_filename ?? originalFilename;
          if (pdfBase64) {
            cachePdf(runId, pdfBase64);
          }
        }

        if (!pdfBase64) {
          throw new Error("PDF non disponibile per questa analisi.");
        }

        triggerPdfDownload(
          pdfBase64,
          buildRunReportFileName({
            kind,
            runId,
            originalFilename,
          }),
        );
      } catch (error) {
        const apiError = error as ApiError;
        if (apiError.status === 401) {
          onUnauthorized();
        }
        throw error;
      } finally {
        setDownloadingPdfRunId(null);
      }
    },
    [analysis?.pdf_base64, cachePdf, onUnauthorized, runStatus],
  );

  return {
    downloadingPdfRunId,
    cachePdf,
    clearPdfCache,
    downloadRunPdf,
  };
}

export function useRunPolling({
  isAuthenticated,
  onUnauthorized,
  refreshRuns,
  runLinks,
  runStatus,
  setAnalysis,
  setPolizzaAnalysis,
  setRunStatus,
  setStatusErrorMessage,
  startCompletedResultRecovery,
}: {
  isAuthenticated: boolean;
  onUnauthorized: (message?: string) => void;
  refreshRuns: RefreshRunsFn;
  runLinks: RunLinks | null;
  runStatus: UnifiedRunStatus | null;
  setRunStatus: Dispatch<SetStateAction<UnifiedRunStatus | null>>;
  setStatusErrorMessage: (value: string | null) => void;
  startCompletedResultRecovery: (
    kind: DocumentKind,
    id: number,
    links: RunLinks | null,
  ) => void;
} & SetResultState) {
  const [isPollingActive, setPollingActiveState] = useState(false);
  const [isPollingSyncing, setPollingSyncingState] = useState(false);
  const pollingInFlightRef = useRef(false);
  const pollingAbortRef = useRef<AbortController | null>(null);
  const pollingRequestSeqRef = useRef(0);
  const activeRunId = runStatus?.id ?? null;
  const activeRunKind = runStatus?.kind ?? null;
  const activeRunStatus = runStatus?.status ?? null;
  const shouldPoll =
    isAuthenticated &&
    activeRunId !== null &&
    (activeRunStatus === "queued" || activeRunStatus === "processing");

  const syncPollingActive = useCallback((value: boolean) => {
    setPollingActiveState(value);
  }, []);

  const syncPollingSyncing = useCallback((value: boolean) => {
    setPollingSyncingState(value);
  }, []);

  useEffect(() => {
    if (!shouldPoll) {
      pollingAbortRef.current?.abort();
      pollingAbortRef.current = null;
      syncPollingActive(false);
      syncPollingSyncing(false);
      pollingInFlightRef.current = false;
      return;
    }

    let cancelled = false;

    const pollOnce = async () => {
      if (cancelled || pollingInFlightRef.current) return;
      if (activeRunId === null || !activeRunKind) return;
      const requestSeq = pollingRequestSeqRef.current + 1;
      pollingRequestSeqRef.current = requestSeq;
      const abortController = new AbortController();

      pollingInFlightRef.current = true;
      pollingAbortRef.current = abortController;
      syncPollingActive(true);
      syncPollingSyncing(true);

      try {
        const links = activeRunKind === "fatture_ddt" ? runLinks : null;
        const latest =
          activeRunKind === "fatture_ddt"
            ? links
              ? await getRunStatusByUrl(links.statusUrl, {
                  signal: abortController.signal,
                })
              : await getRunStatus(activeRunId, {
                  signal: abortController.signal,
                })
            : await getPolizzaStatus(activeRunId, {
                signal: abortController.signal,
              });

        if (
          cancelled ||
          abortController.signal.aborted ||
          pollingRequestSeqRef.current !== requestSeq
        ) {
          return;
        }

        const nextRunStatus = toUnifiedRunStatus(activeRunKind, latest);
        setRunStatus((previous) =>
          areRunStatusesEqual(previous, nextRunStatus)
            ? previous
            : nextRunStatus,
        );
        setStatusErrorMessage(null);

        if (latest.status === "completed") {
          setAnalysis(null);
          setPolizzaAnalysis(null);
          startCompletedResultRecovery(
            activeRunKind,
            activeRunId,
            activeRunKind === "fatture_ddt" ? links : null,
          );
          const refreshResult = await refreshRuns({ silent: true });
          if (!refreshResult.ok && refreshResult.errorMessage) {
            setStatusErrorMessage(refreshResult.errorMessage);
          }
          syncPollingActive(false);
        }

        if (latest.status === "failed") {
          setStatusErrorMessage(latest.last_error ?? "Elaborazione fallita.");
          const refreshResult = await refreshRuns({ silent: true });
          if (!refreshResult.ok && refreshResult.errorMessage) {
            setStatusErrorMessage(refreshResult.errorMessage);
          }
          syncPollingActive(false);
        }
      } catch (error) {
        if (isAbortError(error)) {
          return;
        }

        const apiError = error as ApiError;
        if (apiError.status === 401) {
          if (!cancelled) {
            onUnauthorized();
          }
          return;
        }
        if (!cancelled) {
          setStatusErrorMessage(
            apiError.message ?? "Impossibile aggiornare lo stato.",
          );
        }
      } finally {
        if (pollingRequestSeqRef.current === requestSeq) {
          pollingInFlightRef.current = false;
          pollingAbortRef.current = null;
        }
        if (!cancelled && pollingRequestSeqRef.current === requestSeq) {
          syncPollingSyncing(false);
        }
      }
    };

    void pollOnce();
    const intervalId = window.setInterval(() => {
      void pollOnce();
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      pollingAbortRef.current?.abort();
      syncPollingSyncing(false);
    };
  }, [
    activeRunId,
    activeRunKind,
    onUnauthorized,
    refreshRuns,
    runLinks,
    shouldPoll,
    setAnalysis,
    setPolizzaAnalysis,
    setRunStatus,
    setStatusErrorMessage,
    startCompletedResultRecovery,
    syncPollingActive,
    syncPollingSyncing,
  ]);

  return {
    isPollingActive,
    isPollingSyncing,
    resetPollingState: useCallback(() => {
      pollingAbortRef.current?.abort();
      pollingAbortRef.current = null;
      pollingInFlightRef.current = false;
      syncPollingActive(false);
      syncPollingSyncing(false);
    }, [syncPollingActive, syncPollingSyncing]),
  };
}
